import React, { useState, useEffect, useRef, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { withTranslation } from 'react-i18next';

import { PrintingModal } from '../../../../utils/complexDocumentConstants';
import FunnelChart from '../../FunnelChart';

import { DocumentPanelContainer } from './styles';

import { STATUS_MONITORING, DOCUMENT_PANEL as trans } from '../TranslationValues';
import { emailFunnelChartLabels, metaSearchKeyMappings, stageStatus } from '../../../../utils/constants/statusMonitoring';
import { getChannelTotals, updateFunnelChartData } from '../../../../utils/helper';
import { formatDataForApexBarChart, transformMetaSearchKeys } from '../../../../utils/helper';
import { getAllLanguage } from '../../../../utils/services/languageApi';
import { getUser } from '../../../../utils/services/userAPI';
import { channels } from '../../../../utils/constants/statusMonitoring';
import { useSubscription } from '@apollo/client';
import { getSearchMetaKeys, getExclusions } from '../../../../utils/services/statusMonitoringAPI';
import { FETCH_FUNNEL_CHART } from '../../gql/FetchFunnelChart';

const namespaces = ['SEARCHABLE_TABLE', 'DOCUMENT_PANEL', 'EMAIL_DASHBOARD', 'STATUS_MONITORING'];

const EmailPanel = ({
  jobId,
  activeItem,
  t,
  contentType,
  setStatsFilter,
  chartRef,
  totalStats,
  statsFilter,
  emailBarChartRef,
  staticStatsKeys = [],
  selectedSearchFilters,
}) => {
  const keyNames = metaSearchKeyMappings(t);
  const batchStats = activeItem?.batchStats || []
  const channelTotals = useMemo(() => getChannelTotals(batchStats), [batchStats]);
  const { data } = useSubscription(FETCH_FUNNEL_CHART, {
    variables: {
      batchId: jobId
    },
    skip: false,
    shouldResubscribe: true,
  });

  const labels = useMemo(() => emailFunnelChartLabels(t) ,[]);
  const { sent: sentLabelValue, ...remainingLabels } = labels;
  const remainingFunnelValues = Object.values(remainingLabels);
  const bouncedCountStr = data?.funnelChart?.bounced;
  const bouncedCount = isNaN(Number(bouncedCountStr)) ? 0 : Number(bouncedCountStr);
  const chartData = {
    series: [totalStats.success, totalStats.failure, bouncedCount],
    options: {
      labels: stageStatus(t, 'email').map(status => status.key),
      colors: ['#C8E066', '#35346A', '#6CE5E8'],
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '50%',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        offsetY: 0,
        offsetX: 0,
        formatter: function(val, opts) {
          console.log({ val, opts });
          return (
            '<span style="font-family: Lato"><strong>' +
            val +
            ': ' +
            opts.w.globals.series[opts.seriesIndex] +
            '</strong></span>'
          );
        },
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        events: {
          dataPointSelection: function(event, chartContext, opts) {
            let updatedList = [...stateRef.current];
            const labels = opts.w.config.labels;
            const dataPointIndex = opts.dataPointIndex;

            const isNotSentOrFailure = updatedList.some(
              ({ key, value }) => key === keyNames.status && !labels.includes(value)
            );
            
            if (isNotSentOrFailure) {
              updatedList = updatedList.filter(
                ({ key, value }) => key !== keyNames.status || labels.includes(value)
              );
            }

            if (!updatedList.some(item => item.value === labels[dataPointIndex])) {
              updatedList.push({
                key: keyNames.status,
                value: labels[dataPointIndex],
              });
            } else {
              updatedList = updatedList.filter(item => item.value !== labels[dataPointIndex]);
            }
            stateRef.current = updatedList;
            setStatsFilter(updatedList);
          },
        },
      },
      states: {
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'darken',
          },
        },
      },
    },
  };

  const funnelChartData = useMemo(() => {
    return updateFunnelChartData(data?.funnelChart, channelTotals.email, t);
  }, [data?.funnelChart]);
  const funnelChartColors = ['#2463eb', '#93b1f4', '#d4e1fc', '#133176'];

  const stateRef = React.useRef(statsFilter);
  useEffect(() => {
    stateRef.current = statsFilter;
  }, [statsFilter]);
  const [languageList, setLanguageList] = useState([]);
  const toggleDataPointPreventRerenderRef = useRef(false);
  const { barChartData, categories } = formatDataForApexBarChart(
    JSON.parse(activeItem?.languageStats)?.email || [],
    t,
    languageList,
    contentType,
  );

  const [valueDataSet, setValueDataSet] = useState({
    [metaSearchKeyMappings(t).channel]: channels(t),
    [metaSearchKeyMappings(t).status]: stageStatus(t),
  });
  const [metaKeyRefs, setMetaKeyRefs] = useState({});

  const BarData = {
    options: {
      chart: {
        type: 'bar',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (!toggleDataPointPreventRerenderRef.current) {
              toggleDataPointPreventRerenderRef.current = true;
              const seriesIndex = config.seriesIndex;
              const dataPointIndex = config.dataPointIndex;
              const selectedLabel = config.w.globals.labels[dataPointIndex];

              let updatedList = [...stateRef.current];

              if (!updatedList.some(item => item.value === selectedLabel)) {
                updatedList.push({
                  key: keyNames.language,
                  value: selectedLabel,
                  seriesIndex: seriesIndex,
                });
              } else {
                updatedList = updatedList.filter(item => item.value !== selectedLabel);
              }

              stateRef.current = updatedList;
              setStatsFilter(stateRef.current);
              chartContext.toggleDataPointSelection(seriesIndex === 0 ? 1 : 0, dataPointIndex);
            } else {
              toggleDataPointPreventRerenderRef.current = false;
            }
          },
        },
        toolbar: {
          show: false,
          tools: {
            download: false,
          },
          export: {},
        },
      },
      colors: ['#C8E066', '#35346A'],
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: false,
        offsetX: -6,
        style: {
          fontSize: '12px',
          colors: ['#000'],
        },
        formatter: function(val) {
          if (val >= 1000000) {
            // more than one million
            return (val / 1000000).toFixed(1) + 'M';
          } else if (val >= 1000) {
            // more than one thousands
            return (val / 1000).toFixed(1) + 'k';
          } else {
            return val;
          }
        },
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff'],
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function(val, { series, seriesIndex, dataPointIndex, w }) {
            if (val >= 1000000) {
              // more than one millions
              return (val / 1000000).toFixed(1) + 'M';
            } else if (val >= 1000) {
              // more than one thousands
              return (val / 1000).toFixed(1) + 'k';
            } else if (val < 1000) {
              // more than one thousands
              return val.toFixed(0);
            } else {
              return val;
            }
          },
        },
      },
      xaxis: {
        categories: categories,
        labels: {
          formatter: function(val) {
            if (val >= 1000000) {
              // more than one millions
              return (val / 1000000).toFixed(1) + 'M';
            } else if (val >= 1000) {
              // more than one thousands
              return (val / 1000).toFixed(1) + 'k';
            } else {
              return Math.round(val * 10)/10;
            }
          },
        },
      },
      legend: {
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: false,
        },
      },
      states: {
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'darken',
          },
        },
      },
    },
  };

  const handleFunnelChartClick = (selectedLabels) => {
    const failureChartLabels = stageStatus(t, 'email').map(({ key }) => key);
  
    const statusConditions = Object.fromEntries(
      failureChartLabels.map(status => [
        status,
        stateRef.current.some(item => item.key === keyNames.status && item.value === status),
      ])
    );
  
    if (selectedLabels === sentLabelValue) {
      const isRemainingFunnelStatus = stateRef.current.some(
        ({ key, value }) => key === keyNames.status && remainingFunnelValues.includes(value)
      );
      
      if (isRemainingFunnelStatus) {
        stateRef.current = stateRef.current.filter(
          ({ key, value }) => key !== keyNames.status && !remainingFunnelValues.includes(value)
        );
        setStatsFilter(stateRef.current);
      }
      
      chartRef.current.chart.toggleSeries(failureChartLabels[0]);
    }
  
    if (remainingFunnelValues.includes(selectedLabels)) {
      failureChartLabels.forEach(status => {
        if (statusConditions[status]) chartRef.current.chart.toggleSeries(status);
      });
      
      stateRef.current = [
        { key: keyNames.status, value: selectedLabels },
        ...stateRef.current.filter(item => item.key !== keyNames.status),
      ];
      
      setStatsFilter(stateRef.current);
    }
  };
  

  useEffect(() => {
    if (!jobId) return;

    const fetchAllInfos = async () => {
      try {
        const [metaSearchKeys, users, allLanguages, exclusions] = await Promise.all([
          getSearchMetaKeys(jobId),
          getUser(),
          getAllLanguage(),
          getExclusions(jobId),
        ]);

        let valueMappingDataSet;

        if (metaSearchKeys.statusCode === 5002) {
          const metaKey = metaSearchKeys.data?.length
            ? [...new Set([...staticStatsKeys, ...metaSearchKeys.data])]
            : staticStatsKeys;
          const { transformedKeys, referenceKeysObject } = transformMetaSearchKeys(metaKey, t);
          setMetaKeyRefs(referenceKeysObject);
        }



        if (allLanguages.statusCode === 109 && allLanguages.data) {
          const lang = allLanguages.data.map(({ name, code }) => ({ key: name, value: code }));
          valueMappingDataSet = { ...valueDataSet, [metaSearchKeyMappings(t).language]: lang };
          setValueDataSet(prev => ({ ...prev, [metaSearchKeyMappings(t).language]: lang }));
          setLanguageList(lang);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const fetchLanguages = async () => {
      try {
        const res = await getAllLanguage();
        if (res.statusCode === 109) {
          setLanguageList(res.data.map(({ name, code }) => ({ key: name, value: code })));
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    };

    contentType === PrintingModal ? fetchAllInfos() : fetchLanguages();
  }, [jobId, contentType]);

  return (
    <>
      {channelTotals.email ? (
        <DocumentPanelContainer>
          <div className="chart-wrapper">
            <p className="chart-title">{t(trans.FAILURE_STATS.key, trans.FAILURE_STATS.value)}</p>
            <Chart
              ref={chartRef}
              type="donut"
              options={activeItem ? chartData.options : {}}
              series={activeItem ? chartData.series : []}
              width={'100%'}
            />
          </div>
          <div className="chart-wrapper">
            <div className="chart-container">
              <p className="chart-title">
                {t(trans.LANGUAGES_STATS.key, trans.LANGUAGES_STATS.value)}
              </p>
              <Chart
                type="bar"
                ref={emailBarChartRef}
                options={activeItem ? BarData.options : {}}
                series={activeItem ? barChartData : []}
                width={'100%'}
                height={categories.length > 3 ? 50 * categories.length : undefined}
              />
            </div>
          </div>
          
          <FunnelChart
            funnelChartData={funnelChartData}
            funnelChartColors={funnelChartColors}
            totalEmailConversions={Math.round(data?.funnelChart?.conversionRate || 0)}
            clickable
            onChange={handleFunnelChartClick}
            selected={statsFilter.filter(item => item.key === "Status")
              .map(item => item.value) || []}
          />
        </DocumentPanelContainer>
      ) : (
        <h4>
          {t(STATUS_MONITORING.NO_RECORDS_FOUND.key, STATUS_MONITORING.NO_RECORDS_FOUND.value)}
        </h4>
      )}
    </>
  );
};

export default withTranslation(namespaces)(EmailPanel);
